// frontend/src/components/FlexibilityAddModal.js
// Modal component for adding flexibility data directly in the flowsheet

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Input,
  Space,
  Typography,
  Alert,
  message,
  Divider
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { usePatientChart } from '../context/PatientChartContext';
import MomentDatePicker from './MomentDatePicker.tsx';

const { Title, Text } = Typography;

/**
 * FlexibilityAddModal Component
 * Provides a dedicated interface for adding flexibility data to the flowsheet
 */
const FlexibilityAddModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment());
  const { refreshVitals } = usePatientChart();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedDateTime(moment());
    }
  }, [visible, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form fields
      const values = await form.validateFields();
      
      // Filter out empty values
      const filteredValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          filteredValues[key] = values[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one flexibility parameter.');
        return;
      }

      // Prepare payload for backend - using the biometrics endpoint
      const payload = {
        entryDate: selectedDateTime.toISOString(),
        biometrics: filteredValues
      };

      // Call backend API using the existing biometrics endpoint
      const response = await axios.post(`/patients/${patientId}/flowsheet/biometrics`, payload);

      if (response.data.status === 'success') {
        message.success('Flexibility data saved successfully!');

        // Refresh vitals banner (in case flexibility data affects displayed metrics)
        refreshVitals();

        // Call parent onSave callback to refresh flowsheet
        if (onSave) {
          onSave();
        }

        // Close modal and reset form
        onClose();
        form.resetFields();
      } else {
        throw new Error(response.data.message || 'Failed to save flexibility data');
      }
    } catch (error) {
      console.error('Error saving flexibility data:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to save flexibility data');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Define flexibility parameters based on parameters.md
  const flexibilityFields = [
    { name: 'biometrics_flexibility_armReach_back_right', label: 'Back Scratch Test - Right Arm', units: 'cm', inputType: 'InputNumber', placeholder: 'cm', rules: [{ type: 'number', message: 'Please enter a valid number' }] },
    { name: 'biometrics_flexibility_armReach_back_left', label: 'Back Scratch Test - Left Arm', units: 'cm', inputType: 'InputNumber', placeholder: 'cm', rules: [{ type: 'number', message: 'Please enter a valid number' }] },
    { name: 'biometrics_flexibility_legReach_sitting', label: 'Sit-and-Reach Test', units: 'cm', inputType: 'InputNumber', placeholder: 'cm', rules: [{ type: 'number', message: 'Please enter a valid number' }] },
  ];

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined />
          Add Flexibility Data
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={submitLoading}
          onClick={handleSave}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Save Flexibility Data
        </Button>,
      ]}
      width={800}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Add New Flexibility Entry"
          description="Enter flexibility assessment results and range of motion measurements for the selected date and time. These assessments help track joint mobility and flexibility changes over time."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Date/Time Selection */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Text strong>Entry Date & Time:</Text>
            <br />
            <MomentDatePicker
              showTime
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select date and time"
            />
          </Col>
        </Row>
      </div>

      <Form form={form} layout="vertical" name="flexibility_add_form">
        <Title level={5}>Flexibility Assessment Parameters</Title>
        
        {/* Flexibility Tests */}
        <Divider orientation="left">Flexibility Tests</Divider>
        <Row gutter={16}>
          {flexibilityFields.map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={field.placeholder}
                  precision={1}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Additional Information */}
        <Divider orientation="left">Test Instructions</Divider>
        <Alert
          message="Flexibility Test Guidelines"
          description={
            <div>
              <p><strong>Back Scratch Test:</strong> Measure the distance between fingertips when reaching behind the back. Positive values indicate fingertips overlap, negative values indicate gap between fingertips.</p>
              <p><strong>Sit-and-Reach Test:</strong> Measure the distance reached beyond the toes while sitting. Positive values indicate reaching beyond toes, negative values indicate not reaching the toes.</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </Form>
    </Modal>
  );
};

export default FlexibilityAddModal;
